import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, ViewContainerRef, Component, ViewChild, Inject, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i4 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i5 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as i1 from '@azavista/components/shared';
import { AzavistaSharedModule } from '@azavista/components/shared';
import * as i2 from '@ngx-translate/core';
import * as i3 from '@azavista/components/button';
import { AzavistaButtonModule } from '@azavista/components/button';

class AbstractWidgetDirective {
    constructor() {
        this.settingsChanged = new EventEmitter();
        this.settingsValidChanged = new EventEmitter();
        this.loading = new EventEmitter();
        this.settingsNeeded = new EventEmitter();
        this.customEvent = new EventEmitter();
    }
    ngOnDestroy() {
        this.destroy();
    }
    destroy() {
        this.unsubscribeSubscriptions();
    }
    subscribeToDataChanges(observer) {
        if (this.definition.dataChangedSubject) {
            this.dataChangedSubscription = this.definition.dataChangedSubject.subscribe(observer);
        }
    }
    notifyLoading(loading) {
        this.loading.emit(loading);
    }
    unsubscribeSubscriptions() {
        if (this.dataChangedSubscription) {
            this.dataChangedSubscription.unsubscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AbstractWidgetDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: AbstractWidgetDirective, inputs: { definition: "definition", configMode: "configMode" }, outputs: { settingsChanged: "settingsChanged", settingsValidChanged: "settingsValidChanged", loading: "loading", settingsNeeded: "settingsNeeded", customEvent: "customEvent" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AbstractWidgetDirective, decorators: [{
            type: Directive
        }], propDecorators: { definition: [{
                type: Input
            }], configMode: [{
                type: Input
            }], settingsChanged: [{
                type: Output
            }], settingsValidChanged: [{
                type: Output
            }], loading: [{
                type: Output
            }], settingsNeeded: [{
                type: Output
            }], customEvent: [{
                type: Output
            }] } });

class AzavistaWidgetPlaceholderComponent {
    constructor(componentFactoryResolver) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.settingsChanged = new EventEmitter();
        this.settingsValid = new EventEmitter();
        this.loading = new EventEmitter();
        this.settingsNeeded = new EventEmitter();
        this.customEvent = new EventEmitter();
        this.destroyed$ = new Subject();
    }
    ngOnInit() {
        const cmpFactory = this.componentFactoryResolver.resolveComponentFactory(this.definition.componentType);
        const cmpRef = this.viewChild.createComponent(cmpFactory);
        cmpRef.instance.definition = this.definition;
        cmpRef.instance.configMode = this.configMode;
        cmpRef.instance.settingsChanged.pipe(takeUntil(this.destroyed$)).subscribe({
            next: (data) => this.processSettingsChanged(data)
        });
        cmpRef.instance.settingsValidChanged.pipe(takeUntil(this.destroyed$)).subscribe({
            next: (valid) => this.processSettingsValid(valid)
        });
        cmpRef.instance.loading.pipe(takeUntil(this.destroyed$)).subscribe({
            next: (loading) => this.processLoading(loading)
        });
        cmpRef.instance.settingsNeeded.pipe(takeUntil(this.destroyed$)).subscribe({
            next: () => this.processSettingsNeeded()
        });
        cmpRef.instance.customEvent.pipe(takeUntil(this.destroyed$)).subscribe({
            next: args => this.processCustomEvent(args)
        });
    }
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
    processCustomEvent(args) {
        this.customEvent.next(args);
    }
    processSettingsValid(valid) {
        this.settingsValid.next(valid);
    }
    processLoading(loading) {
        this.loading.next(loading);
    }
    processSettingsChanged(data) {
        this.settingsChanged.next(data);
    }
    processSettingsNeeded() {
        this.settingsNeeded.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetPlaceholderComponent, deps: [{ token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaWidgetPlaceholderComponent, selector: "azavista-widget-placeholder", inputs: { definition: "definition", configMode: "configMode" }, outputs: { settingsChanged: "settingsChanged", settingsValid: "settingsValid", loading: "loading", settingsNeeded: "settingsNeeded", customEvent: "customEvent" }, viewQueries: [{ propertyName: "viewChild", first: true, predicate: ["placeholder"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: `<div #placeholder></div>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetPlaceholderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'azavista-widget-placeholder',
                    template: `<div #placeholder></div>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }]; }, propDecorators: { definition: [{
                type: Input
            }], configMode: [{
                type: Input
            }], settingsChanged: [{
                type: Output
            }], settingsValid: [{
                type: Output
            }], loading: [{
                type: Output
            }], settingsNeeded: [{
                type: Output
            }], customEvent: [{
                type: Output
            }], viewChild: [{
                type: ViewChild,
                args: ['placeholder', { read: ViewContainerRef, static: true }]
            }] } });

class AzavistaWidgetDialogComponent {
    constructor(data, sharedSvc) {
        this.data = data;
        this.sharedSvc = sharedSvc;
        this.settingsAreValid = false;
        this.widgetDefinitionForEdit = {
            title: this.data.widgetDefinition.title,
            settings: this.sharedSvc.clone(this.data.widgetDefinition.settings)
        };
        this.definitionForWidgetPlaceholder = {
            ...this.data.widgetDefinition,
            ...this.widgetDefinitionForEdit,
        };
    }
    close() {
        this.data.actionSubject.next({ action: 'close', data: this.data });
    }
    save() {
        this.data.actionSubject.next({ action: 'save', data: {
                ...this.data,
                widgetDefinition: {
                    ...this.data.widgetDefinition,
                    ...this.widgetDefinitionForEdit
                },
            } });
    }
    onTitleChanged(newTitle) {
        this.safeChange(() => {
            this.widgetDefinitionForEdit.title = newTitle;
            this.settingsAreValid = !!newTitle;
        });
    }
    onSettingsChanged(data) {
        this.safeChange(() => {
            this.settingsAreValid = data.settingsAreValid;
        });
    }
    onSettingsValidChanged(valid) {
        this.safeChange(() => {
            this.settingsAreValid = valid;
        });
    }
    safeChange(fn) {
        window.setTimeout(() => fn());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetDialogComponent, deps: [{ token: MAT_DIALOG_DATA }, { token: i1.AzavistaSharedService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaWidgetDialogComponent, selector: "ng-component", ngImport: i0, template: "<div class=\"aza-cmp-flex-column aza-cmp-padding\">\r\n    <div class=\"widget-title-container\">\r\n        <mat-form-field class=\"title aza-cmp-full-width\">\r\n            <mat-label>\r\n                <span translate>WIDGET_TITLE</span>\r\n            </mat-label>\r\n            <input\r\n                matInput\r\n                [value]=\"widgetDefinitionForEdit.title\"\r\n                (input)=\"onTitleChanged($event.target.value)\">\r\n        </mat-form-field>\r\n    </div>\r\n    <div class=\"widget-container\">\r\n        <azavista-widget-placeholder (settingsChanged)=\"onSettingsChanged($event)\"\r\n            (settingsValidChanged)=\"onSettingsValidChanged($event)\" [definition]=\"definitionForWidgetPlaceholder\"\r\n            [configMode]=\"true\"></azavista-widget-placeholder>\r\n    </div>\r\n    <div mat-dialog-actions class=\"dialog-actions aza-cmp-dialog-actions\">\r\n        <azavista-button (click)=\"close()\" class=\"aza-cmp-dialog-action-cancel-button\"><span translate>CLOSE</span></azavista-button>\r\n        <azavista-button [disabled]=\"!settingsAreValid\" (click)=\"save()\" color=\"primary\" class=\"aza-cmp-dialog-action-apply-button\"><span\r\n                translate>SAVE</span>\r\n        </azavista-button>\r\n    </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i2.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "component", type: i3.AzavistaButtonComponent, selector: "azavista-button", inputs: ["acl", "disabled", "cssClasses", "containerCssClasses", "buttonAttributes"] }, { kind: "component", type: i4.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i4.MatLabel, selector: "mat-label" }, { kind: "directive", type: i5.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "component", type: AzavistaWidgetPlaceholderComponent, selector: "azavista-widget-placeholder", inputs: ["definition", "configMode"], outputs: ["settingsChanged", "settingsValid", "loading", "settingsNeeded", "customEvent"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetDialogComponent, decorators: [{
            type: Component,
            args: [{ template: "<div class=\"aza-cmp-flex-column aza-cmp-padding\">\r\n    <div class=\"widget-title-container\">\r\n        <mat-form-field class=\"title aza-cmp-full-width\">\r\n            <mat-label>\r\n                <span translate>WIDGET_TITLE</span>\r\n            </mat-label>\r\n            <input\r\n                matInput\r\n                [value]=\"widgetDefinitionForEdit.title\"\r\n                (input)=\"onTitleChanged($event.target.value)\">\r\n        </mat-form-field>\r\n    </div>\r\n    <div class=\"widget-container\">\r\n        <azavista-widget-placeholder (settingsChanged)=\"onSettingsChanged($event)\"\r\n            (settingsValidChanged)=\"onSettingsValidChanged($event)\" [definition]=\"definitionForWidgetPlaceholder\"\r\n            [configMode]=\"true\"></azavista-widget-placeholder>\r\n    </div>\r\n    <div mat-dialog-actions class=\"dialog-actions aza-cmp-dialog-actions\">\r\n        <azavista-button (click)=\"close()\" class=\"aza-cmp-dialog-action-cancel-button\"><span translate>CLOSE</span></azavista-button>\r\n        <azavista-button [disabled]=\"!settingsAreValid\" (click)=\"save()\" color=\"primary\" class=\"aza-cmp-dialog-action-apply-button\"><span\r\n                translate>SAVE</span>\r\n        </azavista-button>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: i1.AzavistaSharedService }]; } });

class AzavistaWidgetsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetsModule, declarations: [AzavistaWidgetPlaceholderComponent, AzavistaWidgetDialogComponent], imports: [CommonModule, AzavistaSharedModule, AzavistaButtonModule, MatFormFieldModule, MatInputModule], exports: [AzavistaWidgetPlaceholderComponent, AzavistaWidgetDialogComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetsModule, imports: [CommonModule, AzavistaSharedModule, AzavistaButtonModule, MatFormFieldModule, MatInputModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaWidgetsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AzavistaWidgetPlaceholderComponent, AzavistaWidgetDialogComponent
                    ],
                    imports: [
                        CommonModule, AzavistaSharedModule, AzavistaButtonModule, MatFormFieldModule, MatInputModule
                    ],
                    exports: [
                        AzavistaWidgetPlaceholderComponent, AzavistaWidgetDialogComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractWidgetDirective, AzavistaWidgetDialogComponent, AzavistaWidgetPlaceholderComponent, AzavistaWidgetsModule };
